import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FilterAlt from '@mui/icons-material/FilterAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import RelifIcon from 'assets/images/relif_icon.svg';
import AlertDialog from 'components/UI/AlertDialog';
import { PrimaryBtn } from 'components/UI/Buttons';
import InfoDialog from 'components/UI/InfoDialog';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import OptionsDialog from 'components/UI/OptionsDialog';
import CreateTicketDialog from 'components/common/CreateTicketDialog';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  useCloseLeadMutation,
  useExtractClientInfoMutation,
  useGetCreditStatusMutation,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
  useUpdateEditableAttributesMutation,
  useUploadClientToCRMMutation,
} from 'services/api/clients.api';
import { Client } from 'services/types/client';
import { ALLOWED_CLOSE_LEAD_STATUSES, CloseLeadStatus } from 'services/types/lead';

import TemplateMessageModal from './TemplateMessageModal';

interface Props {
  client: Client;
  refetch: () => void;
  setIsFilteredConversation: (isFiltered: boolean) => void;
  isFilteredConversation: boolean;
  isLoading: boolean;
  isLastLeadClosed: boolean;
}

const ChatHeader: FC<Props> = ({
  client,
  refetch,
  setIsFilteredConversation,
  isFilteredConversation,
  isLoading,
  isLastLeadClosed,
}) => {
  const uploadableBusinessIds = [
    2, 3, 8, 10, 11, 14, 16, 17, 18, 19, 20, 21, 22, 24, 27, 39, 41, 44, 45, 49, 50, 51, 52, 53, 54, 65,
  ];

  const predefinedNotesBusinessUnitIds = [39];

  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openScheduleTemplateModal, setOpenScheduleTemplateModal] = useState<boolean>(false);
  const [openUploadToCRMModal, setOpenUploadToCRMModal] = useState<boolean>(false);
  const [openCRMError, setOpenCRMError] = useState<boolean>(false);
  const [CRMUploadError, setCRMUploadError] = useState<null | string>(null);
  const [openCRMSuccess, setOpenCRMSuccess] = useState<boolean>(false);
  const [openExtractInfoModal, setOpenExtractInfoModal] = useState<boolean>(false);
  const [openExtractInfoError, setOpenExtractInfoError] = useState<boolean>(false);
  const [extractInfoError, setExtractInfoError] = useState<null | string>(null);
  const [openExtractInfoSuccess, setOpenExtractInfoSuccess] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [openNotesModal, setOpenNotesModal] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>(client.notes || '');
  const [anchorElForStatusMenu, setAnchorElForStatusMenu] = React.useState<null | HTMLElement>(null);
  const [selectedStatus, setSelectedStatus] = useState<CloseLeadStatus | null>(null);
  const [openCreateTicketModal, setOpenCreateTicketModal] = useState<boolean>(false);

  const openDropdownMenu = Boolean(anchorEl);
  const openForStatusMenu = Boolean(anchorElForStatusMenu);

  const [updateEditableAttributes] = useUpdateEditableAttributesMutation();
  const [getCreditStatus, { data: creditStatus }] = useGetCreditStatusMutation();
  const [uploadClientToCRM, { isLoading: isUploadClientToCRMLoading }] = useUploadClientToCRMMutation();
  const [extractClientInfo, { isLoading: isExtractClientInfoLoading }] = useExtractClientInfoMutation();
  const [closeLead] = useCloseLeadMutation();
  const [markAsRead] = useMarkAsReadMutation();
  const [markAsUnread] = useMarkAsUnreadMutation();
  const onClickStatusOption = (status: CloseLeadStatus) => {
    setSelectedStatus(status);
    setAnchorElForStatusMenu(null);
  };
  const handleClickForStatusMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElForStatusMenu(event.currentTarget);
  };
  const handleCloseForStatusMenu = () => {
    setAnchorElForStatusMenu(null);
  };
  const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(client.publicChatLink || window.location.href)
      .then(() => {
        setLinkCopied(true);
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error copying link');
      });
  };

  const handleEnableAutomaticResponses = () => {
    updateEditableAttributes({
      id: client.id,
      body: {
        disabledAutomaticResponses: !client.disabledAutomaticResponses,
        ...(client.businessUnitId === 39 && // This is only for the Insurex business unit
          client.disabledAutomaticResponses === true && {
            userId: null,
          }),
      },
    })
      .then(() => {
        refetch();
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error updating client');
      });
  };

  const handleGetCreditStatus = () => {
    getCreditStatus(client.id)
      .then(() => {
        refetch();
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error getting credit status');
      });
  };

  const handleUploadToCRM = () => {
    if (!isUploadClientToCRMLoading) {
      uploadClientToCRM({ clientId: client.id, businessUnitId: client.businessUnitId })
        .then((data) => {
          console.log(data); // eslint-disable-line no-console
          setOpenUploadToCRMModal(false);
          if ('error' in data) {
            setOpenCRMError(true);
            if ('data' in data.error && typeof data.error.data === 'string') {
              setCRMUploadError(data.error.data);
            }
          } else {
            setOpenCRMSuccess(true);
            refetch();
          }
        })
        .catch(() => {
          setOpenUploadToCRMModal(false);
          setOpenCRMError(true);
          setCRMUploadError('Error subiendo cliente al CRM');
        });
    }
  };

  const handleExtractClientInfo = () => {
    if (!isExtractClientInfoLoading) {
      extractClientInfo(client.id)
        .then((data) => {
          console.log(data); // eslint-disable-line no-console
          setOpenExtractInfoModal(false);
          if ('error' in data) {
            setOpenExtractInfoError(true);
            if ('data' in data.error && typeof data.error.data === 'string') {
              setExtractInfoError(data.error.data);
            }
          } else {
            setOpenExtractInfoSuccess(true);
            refetch();
          }
        })
        .catch(() => {
          setOpenExtractInfoModal(false);
          setOpenExtractInfoError(true);
          setExtractInfoError('Error extrayendo información del cliente');
        });
    }
  };

  const handleSaveNotes = () => {
    updateEditableAttributes({
      id: client.id,
      body: { notes },
    })
      .then(() => {
        refetch();
        setOpenNotesModal(false);
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error updating client notes');
      });
  };

  const handleMarkAsRead = () => {
    markAsRead(client.id)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error marking client as read');
      })
      .finally(() => {
        refetch();
      });
  };

  const handleMarkAsUnread = () => {
    markAsUnread(client.id)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error marking client as unread');
      })
      .finally(() => {
        refetch();
      });
  };

  useEffect(() => {
    if (selectedStatus) {
      closeLead({
        id: client.id,
        closedState: selectedStatus,
      })
        .then(() => {
          navigate('/clients');
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Error updating client');
        });
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (creditStatus) {
      alert(JSON.stringify(creditStatus)); // eslint-disable-line no-alert
    }
  }, [creditStatus]);

  useEffect(() => {
    if (linkCopied) {
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    }
  }, [linkCopied]);

  return (
    <Grid item xs={12} px={2}>
      <LoadingOverlay visible={isUploadClientToCRMLoading || isExtractClientInfoLoading} />
      {openCreateTicketModal && (
        <CreateTicketDialog
          open
          onClose={() => setOpenCreateTicketModal(false)}
          clientId={client.id}
          businessUnitId={client.businessUnitId}
          isAdmin={currentUser?.isAdmin}
        />
      )}
      {openScheduleTemplateModal && (
        <TemplateMessageModal
          clientId={client.id}
          businessUnitId={client.businessUnitId}
          open
          setOpen={setOpenScheduleTemplateModal}
          refetch={refetch}
        />
      )}
      <Modal open={openNotesModal} onClose={() => setOpenNotesModal(false)}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', lg: '700px' },
            bgcolor: 'background.paper',
            borderRadius: 6,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box>
            <Typography variant="h6" mb={3}>
              {predefinedNotesBusinessUnitIds.includes(client.businessUnitId) ? 'Seleccionar tag' : 'Editar notas'}
            </Typography>
            {predefinedNotesBusinessUnitIds.includes(client.businessUnitId) ? (
              <Stack spacing={2}>
                {[
                  'VENTA OK',
                  'PENDIENTE FIRMA',
                  'FALTA PÓLIZA',
                  'PENDIENTE DPS',
                  'PENDIENTE FECHA OTORGAMIENTO',
                  'NO CALIFICA',
                  'NO ACEPTA',
                ].map((option) => (
                  <Button
                    key={option}
                    variant={notes === option ? 'contained' : 'outlined'}
                    color={notes === option ? 'primary' : 'inherit'}
                    onClick={() => setNotes(option)}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {option}
                  </Button>
                ))}
              </Stack>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ingresa tus notas aquí..."
                variant="outlined"
              />
            )}
            <Box display="flex" justifyContent="space-between" mt={3}>
              <PrimaryBtn onClick={() => setOpenNotesModal(false)} variant="outlined" color="inherit">
                Cancelar
              </PrimaryBtn>
              <PrimaryBtn onClick={handleSaveNotes} variant="outlined" disabled={notes === client.notes}>
                Guardar
              </PrimaryBtn>
            </Box>
          </Box>
        </Paper>
      </Modal>
      <OptionsDialog
        openDialog={openUploadToCRMModal && !isUploadClientToCRMLoading}
        setOpenDialog={setOpenUploadToCRMModal}
        header="Subir lead al CRM"
        description="¿Estás seguro que deseas subir este lead al CRM?"
      >
        <PrimaryBtn color="error" onClick={() => setOpenUploadToCRMModal(false)}>
          Cancelar
        </PrimaryBtn>
        <PrimaryBtn onClick={handleUploadToCRM}>Subir</PrimaryBtn>
      </OptionsDialog>

      <OptionsDialog
        openDialog={openExtractInfoModal && !isExtractClientInfoLoading}
        setOpenDialog={setOpenExtractInfoModal}
        header="Extraer información del cliente"
        description="¿Deseas ejecutar los agentes de IA para extraer y actualizar la información del cliente?"
      >
        <PrimaryBtn color="error" onClick={() => setOpenExtractInfoModal(false)}>
          Cancelar
        </PrimaryBtn>
        <PrimaryBtn onClick={handleExtractClientInfo}>Extraer</PrimaryBtn>
      </OptionsDialog>
      <AlertDialog
        openDialog={openCRMError}
        setOpenDialog={setOpenCRMError}
        header="Error"
        msg={CRMUploadError || 'Error subiendo cliente al CRM'}
        cancelMsg="Cerrar"
      />
      <AlertDialog
        openDialog={openExtractInfoError}
        setOpenDialog={setOpenExtractInfoError}
        header="Error"
        msg={extractInfoError || 'Error extrayendo información del cliente'}
        cancelMsg="Cerrar"
      />

      {openCRMSuccess && <InfoDialog header="Cliente subido al CRM" msg="Cliente subido al CRM con éxito!" />}
      {openExtractInfoSuccess && (
        <InfoDialog
          header="Información extraída"
          msg="La información del cliente ha sido extraída y actualizada exitosamente!"
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Tooltip title="Recargar conversación" arrow>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <IconButton onClick={refetch} size="large">
                <RefreshIcon />
              </IconButton>
            )}
          </Tooltip>
          <IconButton onClick={handleDropdownClick}>
            <MoreVertIcon />
          </IconButton>

          <Menu
            open={openDropdownMenu}
            onClose={handleDropdownClose}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => {
                setOpenScheduleTemplateModal(true);
                handleDropdownClose();
              }}
            >
              Enviar Template
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenNotesModal(true);
                handleDropdownClose();
              }}
            >
              Editar notas
            </MenuItem>
            {currentUser?.isAdmin && (
              <MenuItem
                onClick={() => {
                  setOpenExtractInfoModal(true);
                  handleDropdownClose();
                }}
              >
                Extraer información
              </MenuItem>
            )}
            <MenuItem
              onClick={handleEnableAutomaticResponses}
              disabled={['ASSIGNED_TO_SALESMAN', 'PENDING_TO_BE_HANDLED', 'HANDLED_BY_SALESMAN'].includes(
                client.lastLeadStatus || ''
              )}
            >
              {client.disabledAutomaticResponses ? 'Habilitar' : 'Deshabilitar'} IA
            </MenuItem>
            {client.creditSimulations && client.creditSimulations.length > 0 && (
              <MenuItem onClick={handleGetCreditStatus}>Estado de simulación de crédito</MenuItem>
            )}
            <MenuItem onClick={handleCopyLink} disabled={linkCopied}>
              {linkCopied ? 'Link copiado' : 'Compartir link'}
            </MenuItem>
            {client.unreadMessagesCount > 0 ? (
              <MenuItem onClick={handleMarkAsRead}>Marcar como leído</MenuItem>
            ) : (
              <MenuItem onClick={handleMarkAsUnread}>Marcar como no leído</MenuItem>
            )}
            {uploadableBusinessIds.includes(client.businessUnitId) && (
              <MenuItem onClick={() => setOpenUploadToCRMModal(true)}>Subir al CRM</MenuItem>
            )}
            <MenuItem onClick={() => setOpenCreateTicketModal(true)}>Crear ticket de soporte</MenuItem>
          </Menu>

          {client.businessUnit?.hasSellerManagement && (
            <>
              <Tooltip
                title={
                  isLastLeadClosed ? 'Lead ya está cerrado' : 'Cerrar lead. No estarás asignado y se habilitará la IA'
                }
                arrow
              >
                <span>
                  <IconButton onClick={handleClickForStatusMenu} color="error" disabled={isLastLeadClosed}>
                    <HighlightOffIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu anchorEl={anchorElForStatusMenu} open={openForStatusMenu} onClose={handleCloseForStatusMenu}>
                {ALLOWED_CLOSE_LEAD_STATUSES.map((status) => (
                  <MenuItem key={status} onClick={() => onClickStatusOption(status)}>
                    {status}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {currentUser?.isAdmin && (
            <Tooltip title="Filtrar conversación" arrow>
              <IconButton onClick={() => setIsFilteredConversation(!isFilteredConversation)} size="large">
                <FilterAlt
                  sx={{
                    color: isFilteredConversation ? 'primary.main' : 'inherit',
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          {currentUser?.isAdmin && client.customAttributes && client.customAttributes.relifId && (
            <IconButton
              size="large"
              onClick={() => {
                if (!client.customAttributes || typeof client.customAttributes.relifId !== 'number') {
                  return;
                }
                const { relifId } = client.customAttributes;
                window.open(
                  `${import.meta.env.VITE_RELIF_URL}/admin/users?selectedUserId=${relifId}&userId=${relifId}`,
                  '_blank'
                );
              }}
            >
              <img src={RelifIcon} alt="Relif" width={30} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default ChatHeader;
